import { useContext, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Typography } from '@mui/material';

import { LocationOn, South, HourglassEmpty, RouteOutlined, Lock } from '@mui/icons-material';

import { DetailedRoute } from '@/generated/model/detailedRoute';
import { time, timeDurationShort } from '@/utilities/timeUtils';
import { sumDistance } from '@/utilities/distanceUtils';
import { numberZeroOrBlank } from '@/utilities/preconditions';
import { ctxRoutes } from '@/contexts/RoutesCtx.tsx';

import './RouteStyle.css';
import FlagsWithGeoAdress from '../common/FlagsWithGeoAddr';

function getHighlightedDuration(routes: DetailedRoute[], highlightedRouteIds?: number[]): number {
  if (highlightedRouteIds && highlightedRouteIds.length > 0) {
    return routes
      .filter((route) => highlightedRouteIds.includes(route.id))
      .reduce((acc, route) => acc + route.durationTimeMicros, 0);
  }

  return routes.reduce((acc, route) => acc + route.durationTimeMicros, 0);
}

function getHighlightedStandTime(routes: DetailedRoute[], highlightedRouteIds?: number[]): number {
  if (highlightedRouteIds && highlightedRouteIds.length > 0) {
    return routes
      .filter((route) => highlightedRouteIds.includes(route.id))
      .reduce((acc, route) => acc + route.standTimeMicros, 0);
  }

  return routes.reduce((acc, route) => acc + route.standTimeMicros, 0);
}

export function RoutesSummary({ ref }: { ref: RefObject<HTMLDivElement | null> }) {
  const ctx = useContext(ctxRoutes);
  const { t } = useTranslation();

  if (!ctx || !ctx.routes || !ctx.routes.length || !ctx.selectedTarget || !ctx.selectedDates) return null;

  const sumDurationTimeMicros: number = getHighlightedDuration(ctx.routes, ctx.highlightedRoutes);
  const sumStandTimeMicros: number = getHighlightedStandTime(ctx.routes, ctx.highlightedRoutes);

  const startRoute =
    ctx.routes.find((route) => {
      if (ctx.highlightedRoutes) return route.id === ctx.highlightedRoutes[0];
    }) ?? ctx.routes[0];

  const stopRoute =
    ctx.routes.find((route) => {
      if (ctx.highlightedRoutes) return route.id === ctx.highlightedRoutes[ctx.highlightedRoutes.length - 1];
    }) ?? ctx.routes[ctx.routes.length - 1];

  const startPosition = startRoute?.startPosition;
  const stopPosition = stopRoute?.stopPosition;

  const hightLightSum = ctx.routes.filter((route) => ctx.highlightedRoutes?.includes(route.id));

  const sum = hightLightSum.length > 0 ? hightLightSum : ctx.routes;

  return (
    <>
      <Card
        ref={ref}
        sx={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
        className="route-summary-card"
        data-qa-element-id="route_summary_panel"
      >
        <CardContent
          className={`route-summary-card-content ${ctx.highlightedRoutes?.length ? 'route-summary-card-content-with-highlight' : ''}`}
        >
          <Typography variant="body2" component="div">
            <div className="route-location">
              <span className="route-time-tag route-time-tag-summary" data-qa-element-id="route_summary_start_time">
                {time(startPosition.timeMicros)}
              </span>
              <span data-qa-element-id="route_summary_start_type_icon">
                {startRoute.privateRoute ? (
                  <Lock className="route-location-icon" />
                ) : (
                  <LocationOn className="route-location-icon" />
                )}
              </span>
              <div className="route-location-address" data-qa-element-id="route_summary_start_address">
                {startRoute.privateRoute && !startPosition.geocodedAddress ? (
                  t('routes.privateRoute')
                ) : (
                  <FlagsWithGeoAdress
                    countryCode={startPosition.countryCode}
                    countryName={startPosition.countryName}
                    geocodedAddress={
                      ctx.poiNamesEnabled && startPosition.poi ? startPosition.poi : startPosition.geocodedAddress
                    }
                  />
                )}
              </div>
            </div>
            <div className="route-distance-details">
              <South className="route-details-type-icon" data-qa-element-id="summary_arrow_down_icon" />
              <div className="route-distance-detail-information">
                <span data-qa-element-id="route_summary_duration">{timeDurationShort(t, sumDurationTimeMicros)}</span>
                <span
                  className="route-text-separator-char"
                  data-qa-element-id="route_summary_duration_length_separator_icon"
                >
                  <RouteOutlined fontSize="small" color="secondary" />
                </span>
                <span data-qa-element-id="route_summary_length">{sumDistance(sum)}</span>
              </div>
            </div>
            <div className="route-location">
              <span className="route-time-tag route-time-tag-destination" data-qa-element-id="route_summary_end_time">
                {time(stopPosition.timeMicros)}
              </span>
              <span data-qa-element-id="route_summary_end_type_icon">
                {stopRoute.privateRoute ? (
                  <Lock className="route-location-icon" />
                ) : (
                  <LocationOn className="route-location-icon" />
                )}
              </span>
              <div className="route-location-address" data-qa-element-id="route_summary_end_address">
                {stopRoute.privateRoute && !stopPosition.geocodedAddress ? (
                  t('routes.privateRoute')
                ) : (
                  <FlagsWithGeoAdress
                    countryCode={stopPosition.countryCode}
                    countryName={stopPosition.countryName}
                    geocodedAddress={
                      ctx.poiNamesEnabled && stopPosition.poi ? stopPosition.poi : stopPosition.geocodedAddress
                    }
                  />
                )}
              </div>
            </div>
            <div
              className="route-distance-details"
              style={{ display: numberZeroOrBlank(sumStandTimeMicros) ? 'none' : 'flex' }}
            >
              <HourglassEmpty className="route-details-type-icon" data-qa-element-id="route_summary_hourglass_icon" />
              <div className="route-distance-detail-information">
                <p data-qa-element-id="route_summary_stop_time_between_routes">
                  {timeDurationShort(t, sumStandTimeMicros)}
                </p>
              </div>
            </div>
          </Typography>
        </CardContent>
      </Card>
    </>
  );
}
